import { useState, useEffect } from "react";
import XPRSurveyLogo from "../../assests/XPRSurveyLogo.png";
import { useNavigate } from "react-router-dom";
import Footer from "../Footer";
import authService from "../../Services/authService";
import {
  FaFacebookF,
  FaTwitter,
  FaInstagram,
  FaLinkedinIn,
} from "react-icons/fa";
import XPRSurveyGreen from "../../assests/XPRSurveyGreen.png";
import { getCaptcha } from "../../Services/toolkitService";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import NormalAlert from "../Alerts/NormalAlert";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [ip, setIp] = useState("");
  const [device, setDevice] = useState("Unknown");
  const navigate = useNavigate();

  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [generalError, setGeneralError] = useState("");

  //captcha States
  const [captcha, setCaptcha] = useState("");
  const [captchaText, setCaptchaText] = useState("");
  const [captchaImage, setCaptchaImage] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");
  const [hexColor, setHexColor] = useState("#90F5CD");
  const [errors, setErrors] = useState({});

  //Alert
  const [alert, setAlert] = useState({
    show: false,
    type: "success",
    message: "",
  });

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
  };

  // Detect IP + Device on mount
  useEffect(() => {
    fetch("https://api64.ipify.org?format=json")
      .then((res) => res.json())
      .then((data) => setIp(data.ip))
      .catch(() => setIp("0.0.0.0"));

    const userAgent = navigator.userAgent.toLowerCase();
    let deviceType = "Unknown";
    if (/windows/i.test(userAgent)) deviceType = "Windows PC";
    else if (/macintosh|mac os x/i.test(userAgent)) deviceType = "Mac";
    else if (/iphone/i.test(userAgent)) deviceType = "iPhone";
    else if (/ipad/i.test(userAgent)) deviceType = "iPad";
    else if (/android/i.test(userAgent)) deviceType = "Android";
    else if (/linux/i.test(userAgent)) deviceType = "Linux";
    else if (/tablet|playbook|silk/i.test(userAgent)) deviceType = "Tablet";
    else if (/mobile/i.test(userAgent)) deviceType = "Mobile";

    setDevice(deviceType);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setLoading(false);
      return;
    }

    try {
      const result = await authService.login(email, password, ip, device);

      if (result.status === "Success" && result.tokenDetails?.userToken) {
        const { userToken, userID, userRole, lastLogin } = result.tokenDetails;

        localStorage.setItem("JWTToken", userToken);
        localStorage.setItem("userID", userID);
        localStorage.setItem("userRole", userRole);
        localStorage.setItem("lastLogin", lastLogin);

        showAlert("success", result.message);

        setTimeout(() => {
          navigate("/survey-list");
        }, 3000);
      } else {
        showAlert("error", result.message || "Invalid credentials ❌");

        await generateCaptcha();
      }
    } catch (error) {
      showAlert(
        "error",
        error.message || "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Invalid email format.";
    }

    if (!password.trim()) {
      newErrors.password = "Password is required.";
    }

    if (!captchaInput.trim()) {
      newErrors.captchaInput = "Captcha is required.";
    } else if (captchaInput.trim() !== captchaText.trim()) {
      newErrors.captchaInput =
        "Please type the captcha exactly as shown (case-sensitive).";
    }
    return newErrors;
  };

  // Generate random captcha
  const generateCaptcha = async () => {
    try {
      const color = hexColor?.startsWith("#")
        ? hexColor
        : `#${hexColor || "90F5CD"}`;
      const data = await getCaptcha(color);
      console.log("Captcha:", data);
      setHexColor(data.hexColor);
      setCaptchaText(data.captchaText);
      setCaptchaImage(data.captchaImage);
    } catch (error) {
      console.log("Error Fetching Captcha:", error);
      setCaptcha("ERROR");
    }
  };

  // Generate captcha on mount
  useEffect(() => {
    generateCaptcha();
  }, []);

  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-[#3bb290]">
        {/* Left Side - Logo */}
        <div className="hidden md:flex flex-1 justify-center items-center">
          <img src={XPRSurveyLogo} alt="Logo" className="w-[400px] h-auto" />
        </div>

        {/* Right Side - Login Box */}
        <div className="flex-1 flex justify-center items-center">
          <div className="bg-white rounded-xl shadow-lg w-[95%] max-w-2xl p-10">
            <h2 className="text-3xl font-bold text-center mb-8">Sign in</h2>
            <form onSubmit={handleLogin}>
              {/* Email */}
              <div className="mb-6">
                <label className="block text-gray-700 mb-2">
                  Registered Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  // className={`w-full border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 ${
                  //   emailError
                  //     ? "border-red-500 focus:ring-red-300"
                  //     : "focus:ring-[#3bb290]"
                  // }`}
                  className={`w-full mt-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#3bb290] ${
                    errors.email ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              {/* Password */}
              <div className="mb-6">
                <label className="block text-gray-700 mb-2">
                  Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className={`w-full mt-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#3bb290] ${
                    errors.password ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.password && (
                  <p className="text-red-500 text-sm mt-1">{errors.password}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-gray-800 font-medium mb-2">
                  Verification Code
                </label>
                <div className="flex items-center flex-wrap gap-3">
                  <div className="me-2">
                    {captchaImage ? (
                      <img
                        src={captchaImage}
                        alt="Captcha"
                        className="w-[150px] h-[45px] rounded-md border border-gray-300 object-cover"
                      />
                    ) : (
                      <div className="bg-gray-100 px-5 py-2 text-lg font-semibold select-none rounded-md border border-gray-300">
                        {captcha || "loading"}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={generateCaptcha}
                    className="text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    <ArrowPathIcon className="w-6 h-6" />
                  </button>

                  <div className="flex-1 mt-2 sm:mt-0">
                    <input
                      type="text"
                      placeholder="Enter Verification Code"
                      value={captchaInput}
                      onChange={(e) => setCaptchaInput(e.target.value)}
                      disabled={loading}
                      className={`w-full mt-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#3bb290] ${
                        errors.captchaInput
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                    />
                    {errors.captchaInput && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.captchaInput}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {generalError && (
                <p className="text-red-600 text-center mb-4">{generalError}</p>
              )}

              {/* Login Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#3bb290] text-white py-3 rounded-lg hover:bg-[#36a281] transition text-lg font-medium"
              >
                {loading ? "Logging in..." : "Login"}
              </button>
            </form>

            {/* Footer Links */}
            <div className="flex justify-between mt-6 text-sm">
              <a href="/" className="text-blue-600 hover:underline">
                Forgot password?
              </a>
              <p>
                Don’t have an account?{" "}
                <a href="/" className="text-blue-600 hover:underline">
                  Register now
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      <footer className="bg-white border-t mt-10">
        <div className="container mx-auto px-6 py-10 grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left">
          {/* Logo + Contact */}
          <div className="flex flex-col items-center md:items-start">
            <img
              src={XPRSurveyGreen}
              alt="Logo"
              className="w-28 mb-4 fill-[#3bb290]"
            />
            <p className="text-gray-700 mb-4">info@expresscleanslate.com</p>
            <div className="flex space-x-4 text-xl text-gray-700">
              <a
                href="#"
                className="hover:text-[#1877F2] transition-colors duration-300"
              >
                <FaFacebookF />
              </a>
              <a
                href="#"
                className="hover:text-[#1DA1F2] transition-colors duration-300"
              >
                <FaTwitter />
              </a>
              <a
                href="#"
                className="hover:text-[#E4405F] transition-colors duration-300"
              >
                <FaInstagram />
              </a>
              <a
                href="#"
                className="hover:text-[#0A66C2] transition-colors duration-300"
              >
                <FaLinkedinIn />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2 text-gray-700">
              <li>
                <a href="#" className="!cursor-pointer hover:text-yellow-600">
                  → Home
                </a>
              </li>
              <li>
                <a href="#" className="cursor-pointer hover:text-yellow-600">
                  → SignIn
                </a>
              </li>
              <li>
                <a href="#" className="cursor-pointer hover:text-yellow-600">
                  → SignUp
                </a>
              </li>
              <li>
                <a href="#" className="cursor-pointer hover:text-yellow-600">
                  → Privacy Policy
                </a>
              </li>
              <li>
                <a href="#" className="cursor-pointer hover:text-yellow-600">
                  → Terms And Condition
                </a>
              </li>
            </ul>
          </div>

          {/* Other Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4">
              Other Services And Products
            </h3>
            <ul className="space-y-2 text-gray-700">
              <li>
                <a
                  href="https://www.opt4solution.site/"
                  className="cursor-pointer hover:text-yellow-600"
                >
                  → Recruitment & Staffing
                </a>
              </li>
              <li>
                <a
                  href="https://www.vrsamadhantech.com/"
                  className="cursor-pointer hover:text-yellow-600"
                >
                  → Tech Solutions
                </a>
              </li>
              <li>
                <a
                  href="https://www.opt4solution.site/"
                  className="cursor-pointer hover:text-yellow-600"
                >
                  → HR & Payroll
                </a>
              </li>
              <li>
                <a
                  href="https://opt4crm.com/"
                  className="cursor-pointer hover:text-yellow-600"
                >
                  → Opt4CRM Portal
                </a>
              </li>
              <li>
                <a
                  href="https://vrsjobs.com/"
                  className="cursor-pointer hover:text-yellow-600"
                >
                  → Job Portal
                </a>
              </li>
              <li>
                <a
                  href="https://www.opt4solution.site/"
                  className="cursor-pointer hover:text-yellow-600"
                >
                  → Other Solutions
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t py-4 text-center text-gray-700 text-sm">
          Design and Developed By :-{" "}
          <span className="font-medium">VRSamadhan Tech Solution</span>
        </div>
      </footer>

      <NormalAlert
        show={alert.show}
        type={alert.type}
        message={alert.message}
        onClose={() => setAlert({ ...alert, show: false })}
      />
    </>
  );
}
